import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Eye, Pencil, Trash2, ShoppingCart, CheckCircle2, Clock, PackageCheck, XCircle, FileImage, Camera } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const STATUS_STYLES = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  commande: { label: 'Commandee', color: 'bg-blue-100 text-blue-700' },
  partiel: { label: 'Partiel', color: 'bg-amber-100 text-amber-700' },
  recu: { label: 'Recu', color: 'bg-green-100 text-green-700' },
  annule: { label: 'Annule', color: 'bg-red-100 text-red-700' },
};

const PurchaseOrdersPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [showReceive, setShowReceive] = useState(null);
  const [receiveData, setReceiveData] = useState([]);
  const [form, setForm] = useState({ supplier_id: '', lines: [], notes: '', expected_date: '' });
  const [activeTab, setActiveTab] = useState('orders');
  const [slipsBySupplier, setSlipsBySupplier] = useState({});
  const [slipsLoading, setSlipsLoading] = useState(false);
  const [previewSlip, setPreviewSlip] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const [ordersRes, suppliersRes, partsRes] = await Promise.all([
        axios.get(`${API}/suppliers/orders/list`, { params }),
        axios.get(`${API}/suppliers/`),
        axios.get(`${API}/inventory/parts`),
      ]);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
      setParts(partsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadSlips = useCallback(async () => {
    setSlipsLoading(true);
    try {
      const res = await axios.get(`${API}/order-slips/by-supplier`);
      setSlipsBySupplier(res.data);
    } catch { console.error('Failed to load slips'); }
    finally { setSlipsLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'slips') loadSlips(); }, [activeTab, loadSlips]);

  const deleteSlip = async (slipId) => {
    if (!window.confirm('Supprimer ce bon de commande ?')) return;
    try {
      await axios.delete(`${API}/order-slips/${slipId}`);
      toast.success('Bon supprime');
      loadSlips();
    } catch { toast.error('Erreur'); }
  };

  // Handle prefill from inventory low-stock quick order
  useEffect(() => {
    const prefill = searchParams.get('prefill');
    if (prefill && suppliers.length > 0) {
      const params = new URLSearchParams(prefill);
      const supplierName = (params.get('supplier') || '').toLowerCase();
      const matchedSupplier = suppliers.find(s => s.name.toLowerCase().includes(supplierName));
      setForm({
        supplier_id: matchedSupplier?.id || '',
        lines: [{
          part_id: params.get('part_id') || '',
          description: params.get('description') || '',
          reference: params.get('reference') || '',
          quantity_ordered: parseFloat(params.get('quantity')) || 1,
          unit_price: parseFloat(params.get('unit_price')) || 0,
        }],
        notes: '',
        expected_date: '',
      });
      setShowForm(true);
      setSearchParams({});
    }
  }, [searchParams, suppliers, setSearchParams]);

  const openCreate = () => {
    setEditOrder(null);
    setForm({ supplier_id: '', lines: [{ description: '', reference: '', quantity_ordered: 1, unit_price: 0, part_id: '' }], notes: '', expected_date: '' });
    setShowForm(true);
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { description: '', reference: '', quantity_ordered: 1, unit_price: 0, part_id: '' }] }));
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const updateLine = (idx, key, value) => setForm(f => {
    const newLines = [...f.lines];
    newLines[idx] = { ...newLines[idx], [key]: value };
    return { ...f, lines: newLines };
  });

  const addPartToLine = (idx, part) => {
    updateLine(idx, 'part_id', part.id);
    updateLine(idx, 'description', part.name + (part.brand ? ` ${part.brand}` : ''));
    updateLine(idx, 'reference', part.reference || '');
    updateLine(idx, 'unit_price', part.unit_price || 0);
  };

  const handleSave = async () => {
    if (!form.supplier_id) { toast.error('Fournisseur requis'); return; }
    if (!form.lines.length || !form.lines[0].description) { toast.error('Au moins une ligne requise'); return; }
    try {
      if (editOrder) {
        await axios.put(`${API}/suppliers/orders/${editOrder.id}`, form);
        toast.success('Commande modifiee');
      } else {
        await axios.post(`${API}/suppliers/orders`, form);
        toast.success('Commande creee');
      }
      setShowForm(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleConfirmOrder = async (orderId) => {
    try {
      await axios.put(`${API}/suppliers/orders/${orderId}`, { status: 'commande' });
      toast.success('Commande confirmee');
      loadData();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Annuler cette commande ?')) return;
    try {
      await axios.put(`${API}/suppliers/orders/${orderId}`, { status: 'annule' });
      toast.success('Commande annulee');
      loadData();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Supprimer cette commande ?')) return;
    try {
      await axios.delete(`${API}/suppliers/orders/${orderId}`);
      toast.success('Commande supprimee');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const openReceive = (order) => {
    setShowReceive(order);
    setReceiveData(order.lines.map((l, i) => ({ index: i, quantity: 0, max: l.quantity_ordered - (l.quantity_received || 0) })));
  };

  const handleReceive = async () => {
    const linesToReceive = receiveData.filter(r => r.quantity > 0);
    if (!linesToReceive.length) { toast.error('Aucune quantite a recevoir'); return; }
    try {
      await axios.post(`${API}/suppliers/orders/${showReceive.id}/receive`, { lines: linesToReceive });
      toast.success('Reception enregistree - stock mis a jour');
      setShowReceive(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur reception'); }
  };

  const loadDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/suppliers/orders/${id}`);
      setShowDetail(res.data);
    } catch (err) { toast.error('Erreur'); }
  };

  const openEdit = (order) => {
    setEditOrder(order);
    setForm({ supplier_id: order.supplier_id, lines: order.lines, notes: order.notes || '', expected_date: order.expected_date || '' });
    setShowForm(true);
  };

  const calcSubtotal = () => form.lines.reduce((s, l) => s + (parseFloat(l.quantity_ordered) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-CH') : '-';

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900" data-testid="orders-title">Commandes fournisseurs</h1>
            <p className="text-base text-slate-600 mt-1">Passer et suivre les commandes de pieces</p>
          </div>
          {activeTab === 'orders' && (
            <Button onClick={openCreate} data-testid="create-order-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle commande
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-1" data-testid="purchase-tabs">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === 'orders' ? 'bg-white text-slate-900 border border-b-0 border-slate-200' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-orders"
          >
            <ShoppingCart className="w-4 h-4 inline mr-1.5" />Commandes
          </button>
          <button
            onClick={() => setActiveTab('slips')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === 'slips' ? 'bg-white text-slate-900 border border-b-0 border-slate-200' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-slips"
          >
            <Camera className="w-4 h-4 inline mr-1.5" />Bulletins photo
            {Object.values(slipsBySupplier).flat().length > 0 && (
              <span className="ml-1.5 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">
                {Object.values(slipsBySupplier).flat().length}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'slips' ? (
          /* ====== BULLETINS PHOTO TAB ====== */
          <div className="space-y-6" data-testid="slips-tab-content">
            {slipsLoading ? (
              <div className="text-slate-400 text-center py-20">{t('common.loading')}</div>
            ) : Object.values(slipsBySupplier).flat().length === 0 ? (
              <Card className="p-12 text-center">
                <FileImage className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucun bulletin de commande scanne</p>
                <p className="text-xs text-slate-400 mt-1">Les mecaniciens peuvent scanner les bons de commande depuis la fiche de travail atelier</p>
              </Card>
            ) : (
              ['ESA', 'Jacquet', 'Automotive'].map(supplier => {
                const slips = slipsBySupplier[supplier] || [];
                if (slips.length === 0) return null;
                const borderColor = supplier === 'ESA' ? 'border-l-blue-500' : supplier === 'Jacquet' ? 'border-l-amber-500' : 'border-l-emerald-500';
                const badgeColor = supplier === 'ESA' ? 'bg-blue-100 text-blue-700' : supplier === 'Jacquet' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
                return (
                  <Card key={supplier} className={`p-4 border-l-4 ${borderColor}`} data-testid={`slips-supplier-${supplier}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${badgeColor}`}>{supplier}</span>
                        <span className="text-slate-400 font-normal">({slips.length} bulletin{slips.length > 1 ? 's' : ''})</span>
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {slips.map(slip => (
                        <div key={slip.id} className="bg-slate-50 rounded-lg p-3 flex gap-3" data-testid={`admin-slip-${slip.id}`}>
                          <button onClick={() => setPreviewSlip(slip)}
                            className="w-20 h-20 rounded-lg bg-white border border-slate-200 overflow-hidden shrink-0 cursor-pointer hover:opacity-80">
                            <img src={`${API}/order-slips/file/${slip.filename}`} alt="Bon" className="w-full h-full object-cover" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-900">{slip.wo_number}</p>
                            <p className="text-xs text-slate-600">{slip.vehicle_info}</p>
                            <p className="text-xs text-slate-500">{slip.client_name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] text-slate-400">{new Date(slip.created_at).toLocaleString('fr-CH')}</span>
                              <span className="text-[10px] text-slate-400">par {slip.uploaded_by}</span>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => deleteSlip(slip.id)}
                            className="text-red-400 hover:text-red-600 h-7 w-7 p-0 shrink-0" data-testid={`admin-delete-slip-${slip.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })
            )}
            {/* Preview dialog */}
            {previewSlip && (
              <Dialog open={!!previewSlip} onOpenChange={() => setPreviewSlip(null)}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>
                      {previewSlip.supplier} — {previewSlip.wo_number} — {previewSlip.vehicle_info}
                    </DialogTitle>
                  </DialogHeader>
                  <img src={`${API}/order-slips/file/${previewSlip.filename}`} alt="Bon de commande"
                    className="w-full rounded-lg" />
                  <p className="text-xs text-slate-500 text-center">
                    Scanne par {previewSlip.uploaded_by} le {new Date(previewSlip.created_at).toLocaleString('fr-CH')}
                  </p>
                </DialogContent>
              </Dialog>
            )}
          </div>
        ) : (
        <>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {['all', 'brouillon', 'commande', 'partiel', 'recu', 'annule'].map(s => (
            <Button key={s} size="sm" variant={filterStatus === s ? 'default' : 'outline'}
              onClick={() => setFilterStatus(s)} data-testid={`filter-${s}`}>
              {s === 'all' ? 'Toutes' : STATUS_STYLES[s]?.label || s}
            </Button>
          ))}
        </div>

        {/* Orders list */}
        {loading ? (
          <div className="text-slate-400 text-center py-20">{t('common.loading')}</div>
        ) : orders.length === 0 ? (
          <Card className="p-12 text-center">
            <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune commande</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.map(o => (
              <Card key={o.id} className="p-4" data-testid={`order-${o.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-lg font-bold text-slate-900">{o.number}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[o.status]?.color || 'bg-slate-100'}`}>
                      {STATUS_STYLES[o.status]?.label || o.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <span>{o.supplier_name}</span>
                    <span className="font-semibold text-slate-900">CHF {o.subtotal?.toFixed(2)}</span>
                    <span>{o.lines?.length || 0} ligne{(o.lines?.length || 0) > 1 ? 's' : ''}</span>
                    {o.expected_date && <span className="text-xs text-slate-400">Prevue: {formatDate(o.expected_date)}</span>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => loadDetail(o.id)} className="text-slate-500 hover:text-blue-600">
                      <Eye className="w-4 h-4" />
                    </Button>
                    {o.status === 'brouillon' && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(o)} className="text-slate-500 hover:text-blue-600">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" onClick={() => handleConfirmOrder(o.id)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid={`confirm-order-${o.id}`}>
                          Confirmer
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(o.id)} className="text-red-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {(o.status === 'commande' || o.status === 'partiel') && (
                      <>
                        <Button size="sm" onClick={() => openReceive(o)} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`receive-order-${o.id}`}>
                          <PackageCheck className="w-3 h-3 mr-1" /> Recevoir
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleCancel(o.id)} className="text-red-400 hover:text-red-600">
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      {/* Create/Edit Order Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editOrder ? 'Modifier commande' : 'Nouvelle commande'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Fournisseur *</Label>
                <Select value={form.supplier_id} onValueChange={v => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="order-supplier-select">
                    <SelectValue placeholder="Choisir..." />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Date prevue</Label>
                <Input type="date" value={form.expected_date} onChange={e => setForm({ ...form, expected_date: e.target.value })} className="mt-1" />
              </div>
            </div>

            {/* Lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-slate-500 uppercase tracking-wider">Lignes de commande</Label>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="add-order-line">
                  <Plus className="w-3 h-3 mr-1" /> Ligne
                </Button>
              </div>
              <div className="space-y-2">
                {form.lines.map((line, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-2 rounded-lg">
                    <div className="col-span-4">
                      {idx === 0 && <Label className="text-xs text-slate-400">{t('common.description')}</Label>}
                      <div className="relative">
                        <Input value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)} placeholder="Piece..." className="text-sm" />
                        {/* Quick part picker */}
                        {line.description && line.description.length >= 2 && !line.part_id && (
                          <div className="absolute z-10 top-full left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 max-h-32 overflow-y-auto">
                            {parts.filter(p => p.name.toLowerCase().includes(line.description.toLowerCase()) || (p.reference || '').toLowerCase().includes(line.description.toLowerCase())).slice(0, 5).map(p => (
                              <button key={p.id} onClick={() => addPartToLine(idx, p)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b last:border-0">
                                <span className="font-medium">{p.name}</span> {p.reference && <span className="text-slate-400">({p.reference})</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs text-slate-400">Reference</Label>}
                      <Input value={line.reference} onChange={e => updateLine(idx, 'reference', e.target.value)} placeholder="Ref." className="text-sm" />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs text-slate-400">Quantite</Label>}
                      <Input type="number" value={line.quantity_ordered} onChange={e => updateLine(idx, 'quantity_ordered', parseFloat(e.target.value) || 0)} className="text-sm" />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs text-slate-400">Prix unit.</Label>}
                      <Input type="number" step="0.01" value={line.unit_price} onChange={e => updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)} className="text-sm" />
                    </div>
                    <div className="col-span-1 text-right text-sm font-medium text-slate-700 pb-2">
                      {((parseFloat(line.quantity_ordered) || 0) * (parseFloat(line.unit_price) || 0)).toFixed(2)}
                    </div>
                    <div className="col-span-1 pb-1">
                      {form.lines.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} className="text-red-400 hover:text-red-600 h-8 w-8 p-0">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-right mt-2 text-sm font-semibold text-slate-900">
                Total: CHF {calcSubtotal().toFixed(2)}
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500">Notes</Label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border rounded-lg p-2 text-sm h-16 mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} data-testid="save-order-btn">{editOrder ? 'Modifier' : 'Creer'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-blue-600" />
                  {showDetail.number} — {showDetail.supplier_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[showDetail.status]?.color || ''}`}>
                    {STATUS_STYLES[showDetail.status]?.label || showDetail.status}
                  </span>
                  {showDetail.order_date && <span className="text-sm text-slate-500">Commandee le {formatDate(showDetail.order_date)}</span>}
                  {showDetail.expected_date && <span className="text-sm text-slate-500">Prevue le {formatDate(showDetail.expected_date)}</span>}
                  {showDetail.received_date && <span className="text-sm text-green-600">Recue le {formatDate(showDetail.received_date)}</span>}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-slate-500">
                      <th className="text-left py-2">{t('common.description')}</th>
                      <th className="text-left py-2">Ref.</th>
                      <th className="text-right py-2">Cmd.</th>
                      <th className="text-right py-2">Recu</th>
                      <th className="text-right py-2">Prix</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showDetail.lines?.map((l, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2">{l.description}</td>
                        <td className="py-2 text-slate-500">{l.reference}</td>
                        <td className="py-2 text-right">{l.quantity_ordered}</td>
                        <td className={`py-2 text-right font-medium ${l.quantity_received >= l.quantity_ordered ? 'text-green-600' : l.quantity_received > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                          {l.quantity_received || 0}
                        </td>
                        <td className="py-2 text-right">{l.unit_price?.toFixed(2)}</td>
                        <td className="py-2 text-right font-medium">{l.total?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold">
                      <td colSpan={5} className="py-2 text-right">Total:</td>
                      <td className="py-2 text-right">CHF {showDetail.subtotal?.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
                {showDetail.notes && <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{showDetail.notes}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={!!showReceive} onOpenChange={() => setShowReceive(null)}>
        <DialogContent className="max-w-lg">
          {showReceive && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <PackageCheck className="w-5 h-5 text-green-600" /> Reception {showReceive.number}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <p className="text-sm text-slate-500">Indiquez les quantites recues pour chaque ligne. Le stock sera automatiquement mis a jour.</p>
                {showReceive.lines?.map((l, i) => {
                  const remaining = l.quantity_ordered - (l.quantity_received || 0);
                  if (remaining <= 0) return (
                    <div key={i} className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                      <CheckCircle2 className="w-4 h-4" /> {l.description} — Complet
                    </div>
                  );
                  return (
                    <div key={i} className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{l.description}</p>
                        <p className="text-xs text-slate-400">Cmd: {l.quantity_ordered} | Deja recu: {l.quantity_received || 0} | Restant: {remaining}</p>
                      </div>
                      <Input
                        type="number" min={0} max={remaining}
                        value={receiveData[i]?.quantity || 0}
                        onChange={e => {
                          const val = Math.min(parseFloat(e.target.value) || 0, remaining);
                          setReceiveData(rd => rd.map((r, ri) => ri === i ? { ...r, quantity: val } : r));
                        }}
                        className="w-24 text-sm"
                        data-testid={`receive-qty-${i}`}
                      />
                    </div>
                  );
                })}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowReceive(null)}>{t('common.cancel')}</Button>
                  <Button onClick={handleReceive} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-receive-btn">
                    <PackageCheck className="w-4 h-4 mr-2" /> Confirmer reception
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
        </>
        )}
      </div>
    </AdminLayout>
  );
};

export default PurchaseOrdersPage;
